import { FloatingLabelInput } from "@/components/ui/floating-label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Facebook, Instagram, Mail } from "lucide-react";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { motion } from "motion/react";
import { Link } from "react-router-dom";

const Newsletter = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const { toast } = useToast();
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.3 });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: "שגיאה",
        description: "אנא הזן כתובת מייל",
        variant: "destructive",
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: "שגיאה",
        description: "אנא הזן כתובת מייל תקינה",
        variant: "destructive",
      });
      return;
    }

    if (!consentChecked) {
      toast({
        title: "שגיאה",
        description: "יש לאשר את תנאי ההרשמה לניוזלטר",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Send to webhook only - no database storage
      const { error } = await supabase.functions.invoke('send-to-webhook', {
        body: {
          from: 'newsletter',
          data: {
            email: email.toLowerCase().trim()
          }
        }
      });

      if (error) {
        console.error("Webhook error:", error);
        throw error;
      }

      toast({
        title: "נרשמת בהצלחה!",
        description: "תודה שהצטרפת לניוזלטר שלנו",
      });
      setEmail("");
      setConsentChecked(false);
    } catch (error) {
      console.error("Error subscribing to newsletter:", error);
      toast({
        title: "שגיאה",
        description: "אירעה שגיאה בהרשמה, נסה שוב מאוחר יותר",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section 
      ref={ref as React.RefObject<HTMLElement>}
      className="py-16 px-4 bg-background"
    >
      <div className="container mx-auto max-w-2xl text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-sm text-muted-foreground mb-2" style={{ fontWeight: 300 }}>
            הרשמו למבצעים החמים
          </p>
          <h2 className="text-4xl md:text-5xl mb-8" style={{ fontWeight: 300 }}>
            הצטרפו לניוזלטר
          </h2>
        </motion.div>
        
        <motion.form 
          onSubmit={handleSubmit} 
          className="flex flex-col gap-4 mb-8 max-w-md mx-auto" 
          dir="rtl"
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="flex gap-4">
            <div className="flex-1">
              <FloatingLabelInput 
                id="newsletter-email"
                type="email" 
                label="דוא״ל"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
              />
            </div>
            <Button 
              type="submit"
              className="bg-primary/90 hover:bg-primary text-white" 
              style={{ fontWeight: 300 }}
              disabled={isLoading}
            >
              {isLoading ? "שולח..." : "הרשמה"}
            </Button>
          </div>
          
          <div className="flex items-start gap-3 text-right">
            <Checkbox 
              id="newsletter-consent"
              checked={consentChecked}
              onCheckedChange={(checked) => setConsentChecked(checked === true)}
              className="mt-1"
            />
            <label 
              htmlFor="newsletter-consent" 
              className="text-xs text-muted-foreground leading-relaxed cursor-pointer"
            >
              בעת ההרשמה שלי לניוזלטר אני מאשר/ת שליחת הודעות ועדכונים לתיבת הדוא״ל שלי ואני מאשר/ת שקראתי את התנאים באתר{" "}
              <Link to="/terms" className="text-primary hover:underline">תנאי השימוש</Link>
              {" "}
              <Link to="/privacy" className="text-primary hover:underline">מדיניות הפרטיות</Link>*
            </label>
          </div>
        </motion.form>

        <motion.div 
          className="flex justify-center gap-4"
          initial={{ opacity: 0 }}
          animate={isVisible ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <a 
            href="mailto:info@alicejewelry.net" 
            className="text-muted-foreground hover:text-primary transition-all duration-300 hover:scale-125"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="דוא״ל"
          >
            <Mail className="h-5 w-5" />
          </a>
          <a 
            href="https://www.facebook.com/alicejewelryil" 
            className="text-muted-foreground hover:text-primary transition-all duration-300 hover:scale-125"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="פייסבוק"
          >
            <Facebook className="h-5 w-5" />
          </a>
          <a 
            href="https://www.instagram.com/alice_jewelry.il/" 
            className="text-muted-foreground hover:text-primary transition-all duration-300 hover:scale-125"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="אינסטגרם"
          >
            <Instagram className="h-5 w-5" />
          </a>
          <a 
            href="https://tiktok.com/@alisjewelry" 
            className="text-muted-foreground hover:text-primary transition-all duration-300 hover:scale-125"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="טיקטוק"
          >
            <svg 
              className="h-5 w-5" 
              fill="currentColor" 
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
            </svg>
          </a>
          <a 
            href="https://apps.apple.com/us/app/alice-jewelry/id1544496428" 
            className="text-muted-foreground hover:text-primary transition-all duration-300 hover:scale-125"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="App Store"
          >
            <svg 
              className="h-5 w-5" 
              fill="currentColor" 
              viewBox="0 0 32 32"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M30.62 20.419c-0.239-0.321-0.573-0.559-0.96-0.672l-0.013-0.003c-0.165-0.047-0.357-0.079-0.555-0.088l-0.006-0c-0.21-0.012-0.419-0.011-0.629-0.011h-4.15l-5.485-9.768c-0.697 0.73-1.18 1.671-1.343 2.718l-0.004 0.029c-0.044 0.268-0.069 0.578-0.069 0.893 0 1.050 0.279 2.035 0.766 2.885l-0.015-0.028 6.591 11.738c0.105 0.187 0.209 0.374 0.324 0.554 0.107 0.172 0.224 0.32 0.355 0.456l-0.001-0.001c0.275 0.28 0.639 0.471 1.046 0.528l0.010 0.001c0.070 0.010 0.152 0.015 0.234 0.015 0.339 0 0.655-0.094 0.925-0.257l-0.008 0.005c0.349-0.211 0.618-0.523 0.771-0.894l0.005-0.012c0.090-0.215 0.142-0.466 0.142-0.728 0-0.169-0.022-0.334-0.062-0.49l0.003 0.014c-0.055-0.208-0.125-0.388-0.213-0.558l0.007 0.015c-0.094-0.193-0.2-0.379-0.305-0.565l-1.52-2.707h1.994c0.21 0 0.419 0.001 0.628-0.011 0.203-0.009 0.395-0.041 0.578-0.092l-0.018 0.004c0.4-0.117 0.735-0.355 0.97-0.671l0.003-0.005c0.235-0.316 0.376-0.714 0.376-1.144s-0.141-0.829-0.38-1.15l0.004 0.005zM4.894 24.175l-0.99 1.875c-0.102 0.194-0.205 0.387-0.298 0.588-0.079 0.161-0.148 0.349-0.197 0.544l-0.004 0.020c-0.037 0.149-0.058 0.32-0.058 0.495 0 0.272 0.051 0.533 0.144 0.772l-0.005-0.015c0.15 0.395 0.414 0.719 0.751 0.939l0.007 0.005c0.254 0.165 0.564 0.263 0.898 0.263 0.081 0 0.16-0.006 0.237-0.017l-0.009 0.001c0.412-0.063 0.769-0.262 1.031-0.549l0.001-0.001c0.127-0.139 0.242-0.294 0.339-0.46l0.007-0.013c0.113-0.188 0.214-0.382 0.317-0.576l1.436-2.72c-0.552-0.82-1.476-1.352-2.525-1.352-0.389 0-0.761 0.073-1.103 0.207l0.021-0.007zM12.012 19.648l7.636-13.792c0.105-0.189 0.21-0.377 0.305-0.573 0.081-0.157 0.151-0.34 0.201-0.531l0.004-0.019c0.038-0.145 0.059-0.311 0.059-0.483 0-0.266-0.052-0.519-0.147-0.751l0.005 0.013c-0.156-0.388-0.426-0.704-0.767-0.914l-0.008-0.005c-0.262-0.161-0.578-0.256-0.917-0.256-0.082 0-0.163 0.006-0.243 0.017l0.009-0.001c-0.417 0.059-0.782 0.253-1.055 0.535l-0 0.001c-0.13 0.136-0.247 0.287-0.347 0.449l-0.007 0.012c-0.115 0.183-0.219 0.372-0.324 0.561l-0.483 0.872-0.483-0.872c-0.105-0.189-0.208-0.379-0.323-0.561-0.107-0.174-0.224-0.325-0.355-0.462l0.001 0.001c-0.273-0.283-0.637-0.477-1.045-0.535l-0.010-0.001c-0.070-0.010-0.151-0.015-0.233-0.015-0.339 0-0.656 0.095-0.925 0.26l0.008-0.004c-0.349 0.215-0.619 0.531-0.771 0.906l-0.005 0.013c-0.090 0.219-0.142 0.472-0.142 0.738 0 0.171 0.022 0.338 0.062 0.496l-0.003-0.014c0.055 0.21 0.125 0.393 0.213 0.566l-0.007-0.016c0.094 0.195 0.2 0.384 0.305 0.573l1.56 2.817-6.076 10.975h-4.164c-0.209 0-0.419-0.001-0.628 0.012-0.203 0.009-0.395 0.041-0.578 0.094l0.018-0.004c-0.401 0.119-0.735 0.36-0.969 0.68l-0.004 0.005c-0.235 0.321-0.376 0.724-0.376 1.159s0.141 0.838 0.38 1.165l-0.004-0.006c0.237 0.325 0.571 0.566 0.959 0.681l0.013 0.003c0.165 0.048 0.357 0.080 0.554 0.089l0.006 0c0.209 0.012 0.419 0.011 0.628 0.011h16.367c0.045-0.093 0.087-0.205 0.121-0.321l0.004-0.016c0.078-0.243 0.123-0.523 0.123-0.813 0-1.487-1.185-2.698-2.662-2.74l-0.004-0z"/>
            </svg>
          </a>
          <a 
            href="https://play.google.com/store/apps/details?id=co.median.android.nwbaxj" 
            className="text-muted-foreground hover:text-primary transition-all duration-300 hover:scale-125"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Google Play"
          >
            <svg 
              className="h-5 w-5" 
              fill="currentColor" 
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M1.22 0c-.03.093-.06.185-.06.308v23.229c0 .217.061.34.184.463l11.415-12.093L1.22 0zm12.309 12.708l2.951 3.045-4.213 2.4s-5.355 3.044-8.308 4.739l9.57-10.184zm.801-.831l3.166 3.292c.496-.276 4.371-2.492 4.924-2.8.584-.338.525-.8.029-1.046-.459-.255-4.334-2.475-4.92-2.835l-3.203 3.392.004-.003zm-.803-.8l2.984-3.169-4.259-2.431S5.309 1.505 2.999.179l10.53 10.898h-.002z"/>
            </svg>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Newsletter;
